import { Component, ReactNode } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { withScope, captureException } from '@sentry/browser'
import type { Extras } from '@sentry/core'

import ErrorPage from './ErrorPage'
import ReloadPage from './ReloadPage'

function looksLikeNewVersionError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false
    }
    const { name, message } = error
    return (
        (name === 'ChunkLoadError' &&
            message.includes('Loading chunk') &&
            message.includes('failed')) ||
        (name === 'SyntaxError' && message.includes('Unexpected token'))
    )
}

interface ErrorBoundaryProps {
    children: ReactNode
}

class ErrorBoundary extends Component<ErrorBoundaryProps> {
    state = { error: null }

    componentDidCatch(error: unknown, errorInfo: unknown): void {
        this.setState({ error })
        datadogRum.addError(error)
        withScope((scope) => {
            scope.setExtras(errorInfo as Extras)
            captureException(error)
        })
    }

    render(): ReactNode {
        const { error } = this.state
        const { children } = this.props
        if (error) {
            if (looksLikeNewVersionError(error)) {
                return <ReloadPage />
            }

            return <ErrorPage />
        }
        // when there's not an error, render children untouched
        return children
    }
}

export default ErrorBoundary
